/**
 * <need-tabs> — tab switcher for sections/shop-by-need.liquid.
 *
 * Every panel is server-rendered; this element only toggles which one is visible.
 *   - click on a [role="tab"] selects it
 *   - Arrow keys / Home / End move between tabs (all four arrows work in both layouts)
 *   - on narrow screens the selected chip is scrolled into view
 *   - in the theme editor, selecting a block activates its tab
 *
 * Markup contract:
 *   [role="tablist"] > li[shopify_attributes] > button[role="tab"][aria-controls="<panel id>"]
 *   [role="tabpanel"][id="<panel id>"][hidden]
 */
class NeedTabs extends HTMLElement {
  /** @type {MediaQueryList | null} */
  #desktop = null;
  /** @type {MediaQueryList | null} */
  #reducedMotion = null;
  /** Incremented on every switch so a stale fade-out never hides the panel that was just selected. */
  #switchToken = 0;

  connectedCallback() {
    this.#adoptPanels();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    this.#desktop = window.matchMedia('(min-width: 750px)');
    this.#desktop.addEventListener('change', this.#syncOrientation);
    this.#syncOrientation();
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
    }

    // The server marks the first block as selected; on the storefront that block may have been
    // skipped (no collection), so make sure exactly one rendered tab is selected.
    if (!this.selectedTab) this.select(this.tabs[0], { scroll: false });
  }

  /**
   * Each `_need-tab` block renders its tab and its panel as siblings inside the tablist
   * (theme block settings are not readable from the section). Move the panels into the
   * panels container so the two-column layout and the mobile chip row work.
   */
  #adoptPanels() {
    const panels = this.querySelector('.shop-by-need__panels');
    if (!panels) return;

    for (const panel of this.querySelectorAll('[role="tablist"] [role="tabpanel"]')) {
      panels.append(panel);
    }

    // Each panel holds a grid and a mobile carousel copy; number the cards in each list separately
    for (const list of this.querySelectorAll('.shop-by-need__panel .resource-list')) {
      list.querySelectorAll('.resource-list__item').forEach((item, index) => {
        /** @type {HTMLElement} */ (item).style.setProperty('--card-index', String(index));
      });
    }

    // The CTA comes in after the last card
    for (const panel of this.querySelectorAll('.shop-by-need__panel')) {
      const cta = /** @type {HTMLElement | null} */ (panel.querySelector('.shop-by-need__cta'));
      cta?.style.setProperty('--card-index', String(this.#cardCount(panel)));
    }
  }

  /** Cards in one list of the panel (grid and carousel hold the same products). */
  #cardCount(panel) {
    return panel.querySelector('.resource-list')?.querySelectorAll('.resource-list__item').length ?? 0;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#desktop?.removeEventListener('change', this.#syncOrientation);
    this.#desktop = null;
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @returns {HTMLElement | null} */
  get selectedTab() {
    return this.querySelector('[role="tab"][aria-selected="true"]');
  }

  /**
   * @param {HTMLElement | null} tab
   * @param {{ focus?: boolean, scroll?: boolean }} [options]
   */
  select(tab, { focus = false, scroll = true } = {}) {
    if (!tab) return;

    // A tab behind the "+N" chip (keyboard, theme editor) brings the rest of the row with it
    if (tab.closest('[data-overflow]')) this.#expand();

    const previous = this.selectedTab;
    const nextPanel = this.#panelFor(tab);

    // Clicking the already selected tab: nothing to switch, don't replay the entrance
    if (previous === tab && nextPanel && !nextPanel.hidden) {
      if (focus) tab.focus({ preventScroll: true });
      return;
    }

    const previousPanel = previous && previous !== tab ? this.#panelFor(previous) : null;

    for (const candidate of this.tabs) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = this.#panelFor(candidate);
      if (panel && panel !== previousPanel && panel !== nextPanel) panel.hidden = true;
    }

    this.#swapPanels(previousPanel, nextPanel);

    if (focus) tab.focus({ preventScroll: true });

    if (scroll && !this.#desktop?.matches) {
      tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * Fade the old panel out, then reveal the new one with its cards staggering in (CSS does the
   * drawing; this only sequences the classes). Reduced motion → plain show/hide.
   * @param {HTMLElement | null} previousPanel
   * @param {HTMLElement | null} nextPanel
   */
  async #swapPanels(previousPanel, nextPanel) {
    if (!nextPanel) return;
    const token = ++this.#switchToken;
    const animate = !this.#reducedMotion?.matches;

    if (previousPanel && !previousPanel.hidden && animate) {
      previousPanel.classList.add('is-leaving');
      await this.#afterTransition(previousPanel, 200);
      if (token !== this.#switchToken) return;
      previousPanel.classList.remove('is-leaving');
    }

    if (previousPanel) previousPanel.hidden = true;
    nextPanel.hidden = false;

    if (!animate) return;

    nextPanel.classList.remove('is-entering');
    void nextPanel.offsetWidth; // restart the keyframes when the same panel is re-entered
    nextPanel.classList.add('is-entering');
    // Keep the class until the last element (the CTA) has finished: stagger + duration, see CSS
    await new Promise((resolve) => setTimeout(resolve, this.#cardCount(nextPanel) * 40 + 650 + 50));
    if (token === this.#switchToken) nextPanel.classList.remove('is-entering');
  }

  /**
   * Resolves on the first `transitionend` bubbling from `el`, or after `timeout` ms.
   * @param {HTMLElement} el
   * @param {number} timeout
   */
  #afterTransition(el, timeout) {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        el.removeEventListener('transitionend', done);
        resolve(undefined);
      };
      const timer = setTimeout(done, timeout);
      el.addEventListener('transitionend', done, { once: true });
    });
  }

  /** @param {HTMLElement} tab */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const more = target.closest('[data-tabs-more]');
    if (more && this.contains(more)) {
      this.#expand()?.focus();
      return;
    }

    const tab = target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.select(/** @type {HTMLElement} */ (tab));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches('[role="tab"]')) return;

    const tabs = this.tabs;
    const index = tabs.indexOf(target);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  };

  /** Theme editor: `event.target` is the element carrying `block.shopify_attributes` (the <li>). */
  #onBlockSelect = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(target)) return;
    this.select(target.querySelector('[role="tab"]'));
  };

  #syncOrientation = () => {
    const list = this.querySelector('[role="tablist"]');
    // gift-sets keeps its chip row at every width and says so with data-orientation
    const orientation = this.dataset.orientation || (this.#desktop?.matches ? 'vertical' : 'horizontal');
    list?.setAttribute('aria-orientation', orientation);
  };

  /** Reveal the tabs hidden behind the "+N" chip (`[data-overflow]`, desktop only). */
  #expand() {
    if (this.hasAttribute('data-expanded')) return;
    const firstHidden = this.querySelector('[data-overflow] [role="tab"]');
    this.setAttribute('data-expanded', '');
    const more = /** @type {HTMLElement | null} */ (this.querySelector('[data-tabs-more]'));
    if (more) more.hidden = true;
    return /** @type {HTMLElement | null} */ (firstHidden);
  }
}

if (!customElements.get('need-tabs')) {
  customElements.define('need-tabs', NeedTabs);
}
